import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RoutesRecognized,
} from '@angular/router';
import { AuthSelectors } from '@NgXs/authentication/selectors/auth.selectors';
import { Store } from '@ngxs/store';
import { Subject, distinctUntilChanged, takeUntil } from 'rxjs';
import { AiChatbotService } from '@shared/services/ai-chatbot/ai-chatbot.service';

const CHATBOT_ROUTE_DATA_KEY = 'showAiChatbot';

/**
 * Security invariant: only authenticated, non-public routes that explicitly set
 * `data.showAiChatbot` may mount the third-party chatbot.
 */
@Component({
  selector: 'og-ai-chatbot',
  standalone: true,
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiChatbotComponent implements OnInit, OnDestroy {
  private readonly destroyed$ = new Subject<void>();
  private isAuthenticated = false;
  private isMounted = false;
  private isNavigating = false;
  private destinationAllowsChatbot = false;

  constructor(
    private readonly router: Router,
    private readonly store: Store,
    private readonly aiChatbotService: AiChatbotService
  ) {}

  ngOnInit(): void {
    this.store
      .select(AuthSelectors.isAuthenticated)
      .pipe(distinctUntilChanged(), takeUntil(this.destroyed$))
      .subscribe((isAuthenticated) => {
        this.isAuthenticated = isAuthenticated;
        this.updateForCurrentRoute();
      });

    this.router.events.pipe(takeUntil(this.destroyed$)).subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.isNavigating = true;
        this.destinationAllowsChatbot = false;
        this.setMounted(false);
        return;
      }

      if (event instanceof RoutesRecognized) {
        this.destinationAllowsChatbot =
          !this.isPublicUrl(event.urlAfterRedirects) && this.routeEnablesChatbot(event.state.root);
        return;
      }

      if (event instanceof NavigationEnd) {
        this.isNavigating = false;
        this.setMounted(this.isAuthenticated && this.destinationAllowsChatbot);
        return;
      }

      if (event instanceof NavigationCancel || event instanceof NavigationError) {
        this.isNavigating = false;
        this.updateForCurrentRoute();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
    this.setMounted(false);
  }

  private setMounted(shouldMount: boolean): void {
    if (shouldMount === this.isMounted) {
      return;
    }

    this.isMounted = shouldMount;
    shouldMount ? this.aiChatbotService.mount() : this.aiChatbotService.unmount();
  }

  private updateForCurrentRoute(): void {
    if (this.isNavigating) {
      this.setMounted(false);
      return;
    }

    this.setMounted(
      this.isAuthenticated &&
        !this.isPublicUrl(this.router.url) &&
        this.routeEnablesChatbot(this.router.routerState.snapshot.root)
    );
  }

  private isPublicUrl(url: string): boolean {
    return url.split('?')[0].startsWith('/public');
  }

  private routeEnablesChatbot(route: ActivatedRouteSnapshot): boolean {
    return (
      route.data?.[CHATBOT_ROUTE_DATA_KEY] === true ||
      route.children.some((child) => this.routeEnablesChatbot(child))
    );
  }
}
